import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { getCountPage } from 'shared/libs/pagination/offset'
import { OrdersStatusKey } from 'shared/types/Common'

import { ordersApi } from '../services/ordersApi'
import { ChangeOrdersParams, OrdersState } from '../types'

const initialState: OrdersState = {
  orders: {
    INITIALIZED: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    CANCELED: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    ACCEPTED: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    COMPLETED: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    ON_THE_WAY: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    PREPARING: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
    WAITING_FOR_COURIER: {
      page: 1,
      limit: 10,
      count: null,
      data: {},
      isUpdate: null,
      isFetching: false,
    },
  },
  restaurant: null,
  isAllReset: false,
}

export const OrdersSlice = createSlice({
  initialState,
  name: 'orders',
  reducers: {
    changeOrdersParams: (
      state,
      { payload }: PayloadAction<ChangeOrdersParams>,
    ) => {
      if (
        payload.status &&
        payload.page &&
        payload.limit &&
        payload.isFetching
      ) {
        const orderStatus = state.orders[payload.status]
        orderStatus.page = payload.page
        orderStatus.limit = payload.limit
        orderStatus.isUpdate = payload.isUpdate ?? null
      }
      if (payload.isFetching && payload.status)
        state.orders[payload.status].isFetching = payload.isFetching
      if (payload.restaurant) state.restaurant = payload.restaurant
    },
    clearOrders: (
      state,
      { payload }: PayloadAction<{ status: OrdersStatusKey }>,
    ) => {
      state.orders[payload.status] = {
        data: {},
        page: 1,
        limit: 10,
        count: null,
        isUpdate: null,
        isFetching: false,
      }
    },
    changeIsAllReset: (state, { payload }: PayloadAction<boolean>) => {
      state.isAllReset = payload
    },
    markOrdersAsSeen: (state, { payload }: PayloadAction<number[]>) => {
      const statuses = Object.keys(state.orders) as OrdersStatusKey[]

      statuses.forEach((status) => {
        const statusData = state.orders[status].data

        Object.keys(statusData).forEach((page) => {
          const pageData = statusData[page]

          if (pageData && pageData.results) {
            pageData.results = pageData.results.map((order) => {
              if (payload.includes(order.id)) {
                return { ...order, seen: true }
              }
              return order
            })
          }
        })
      })
    },
  },
  extraReducers: (build) => {
    build
      .addMatcher(
        ordersApi.endpoints.getOrders.matchFulfilled,
        (state, { payload, meta }) => {
          const { status, offset, limit } = meta.arg.originalArgs.params
          const currentPage = getCountPage({
            limit: (limit && +limit) || 10,
            count: (offset && +offset + 1) || 1,
          })
          state.orders = {
            ...state.orders,
            [status]: {
              ...state.orders[status],
              count: payload.result.count ?? null,
              data: {
                ...(((state.orders[status].isUpdate ||
                  (offset && +offset > 0)) &&
                  state.orders[status].data) ||
                  []),
                [String(currentPage)]: {
                  results: payload.result.results,
                  next: payload.result.next ?? null,
                  previous: payload.result.previous ?? null,
                },
              },
            },
          }
          state.orders[status].isUpdate = null
          state.orders[status].isFetching = false
          state.isAllReset = false
        },
      )
      .addMatcher(
        ordersApi.endpoints.changeOrdersView.matchFulfilled,
        (state, { payload, meta }) => {
          if (payload.success) {
            const orderIds = meta.arg.originalArgs.orders

            OrdersSlice.caseReducers.markOrdersAsSeen(state, {
              type: 'orders/markOrdersAsSeen',
              payload: orderIds,
            })
          }
        },
      )
  },
})

export const { changeOrdersParams, clearOrders, changeIsAllReset } =
  OrdersSlice.actions
export const ordersSlice = OrdersSlice.reducer
